import type { ProductCombinationOptionGroup, ProductInfoItem } from '~~/shared/types/store'
import type { VariantPicks } from '~~/app/utils/quoteVariant'

/**
 * Nazwa i szczegóły karty produktu dla WYBRANEGO wariantu (sklep katalogowy).
 *
 * EasyItem zakłada jeden produkt na model, a nazwę i SKU bierze z kombinacji
 * domyślnej: „BOSTON Bialy 3XL” (S16000-WH-3XL), „VERIA Niebieski” (MO2158-04).
 * Po wyborze innego koloru karta dalej mówiła „Bialy 3XL” i pokazywała SKU
 * białego 3XL (zgłoszenie proles 05.11.2026). Produkty Axpol mają nazwę bez
 * atrybutów („Kubek ceramiczny 330 ml | Everika”) — te zostają bez zmian.
 */

const WORD_CHAR = /[\p{L}\p{N}]/u

function isWordChar(char: string | undefined): boolean {
  return char !== undefined && WORD_CHAR.test(char)
}

/** Ostatnie wystąpienie `needle` jako całego słowa (bez rozróżniania wielkości liter). */
function lastWholeWordIndex(haystack: string, needle: string): number {
  const text = haystack.toLocaleLowerCase('pl')
  const word = needle.toLocaleLowerCase('pl')
  // Porównanie po małych literach zakłada tę samą długość — dla polskich liter tak jest;
  // gdyby nie było (egzotyczne znaki), wolimy nic nie podmieniać niż uciąć nazwę.
  if (!word || text.length !== haystack.length || word.length !== needle.length) return -1

  let from = text.length
  while (from >= 0) {
    const index = text.lastIndexOf(word, from)
    if (index < 0) return -1
    if (!isWordChar(haystack[index - 1]) && !isWordChar(haystack[index + word.length])) return index
    from = index - 1
  }
  return -1
}

function isUpperCase(text: string): boolean {
  return /\p{L}/u.test(text) && text === text.toLocaleUpperCase('pl')
}

interface Replacement {
  start: number
  end: number
  value: string
}

/**
 * Nazwa produktu z wartościami wybranego wariantu w miejscu wartości kombinacji
 * domyślnej. Grupa bez `defaultId`, bez wyboru albo z wartością, której w nazwie
 * nie ma, nie zmienia niczego. Miejsca liczone są na PIERWOTNEJ nazwie, więc
 * podmiana jednej grupy nie trafi w tekst wstawiony dla drugiej.
 */
export function variantProductName(
  name: string,
  options: readonly ProductCombinationOptionGroup[],
  picks: VariantPicks,
): string {
  const replacements: Replacement[] = []

  for (const group of options) {
    const defaultItem = group.items.find(item => item.id === group.defaultId)
    const pickedItem = group.items.find(item => item.id === picks[group.name])
    if (!defaultItem || !pickedItem || pickedItem.id === defaultItem.id) continue

    const needle = defaultItem.value.trim()
    const start = lastWholeWordIndex(name, needle)
    if (start < 0) continue
    const end = start + needle.length
    if (replacements.some(r => start < r.end && r.start < end)) continue

    const matched = name.slice(start, end)
    const value = pickedItem.value.trim()
    replacements.push({ start, end, value: isUpperCase(matched) ? value.toLocaleUpperCase('pl') : value })
  }

  return replacements
    .sort((a, b) => b.start - a.start)
    .reduce((result, r) => result.slice(0, r.start) + r.value + result.slice(r.end), name)
}

/**
 * „Szczegóły produktu i skład” z SKU wybranego wariantu. `sku`:
 *  - `undefined` — brak wariantu, szczegóły bez zmian;
 *  - `''` — wariant bez własnego SKU: wiersz znika, bo SKU produktu należy do
 *    kombinacji domyślnej i przy innym wyborze byłby nieprawdą;
 *  - tekst — podstawiony w istniejącym wierszu SKU (nowego nie dopisujemy).
 */
export function variantProductInfo(
  productInfo: ProductInfoItem[],
  sku: string | undefined,
): ProductInfoItem[] {
  if (sku === undefined) return productInfo

  const isSku = (item: ProductInfoItem) => item.name.trim().toLowerCase() === 'sku'
  if (!sku) return productInfo.filter(item => !isSku(item))

  return productInfo.map(item => (isSku(item) ? { ...item, value: sku } : item))
}
